/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-11 10:04:45
 *
 *******************************************************/
// @flow

import {
  HomeIcon,
  AnalyticsIcon,
  FilterMenuIcon,
  ThumbsUpIcon,
  SetupIcon,
  IntegrationIcon,
  GlobeIcon,
  InsightIcon,
  SyncIcon,
  SettingsIcon,
  SettingsMinorIcon,
  ProfileIcon,
  ProfileMinorIcon,
  PrimaryIcon,
  SearchIcon,
  AIIcon
} from 'src/components/containers/common/icon';
import { routes } from 'src/routes';

export const getMenuLeft = (i18n, conditions = {}) => {
  const { hideAISearch = false, themeSetupV2 = false } = conditions;
  return [
    {
      title: i18n.translate('AdminHTML.MainMenuV2.home'),
      id: 'menu-home',
      icon: HomeIcon,
      routeTo: routes.dashboard.view(),
      isNew: false
    },
    {
      title: i18n.translate('AdminHTML.MainMenuV2.filters'),
      id: 'menu-filters',
      icon: FilterMenuIcon,
      isNew: false,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.setup'),
          icon: SetupIcon,
          id: 'menu-filters-setup',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.filterTrees'),
              isNew: false,
              id: 'menu-filters-filter-trees',
              routeTo: routes.filterTree.list()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.merchandising'),
              isNew: false,
              id: 'menu-filters-merchandising',
              routeTo: `${routes.merchandising.view()}?panelID=collection`
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.filterLayout'),
              isNew: false,
              id: 'menu-filters-layout',
              isHide: !themeSetupV2,
              routeTo: routes.filterLayout.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.displaySettings'),
              isNew: false,
              id: 'menu-filters-display-settings',
              routeTo: routes.filterSettings.view()
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.insights'),
          icon: InsightIcon,
          id: 'menu-filters-insights',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.analytics'),
              isNew: false,
              id: 'menu-analytics-collection',
              routeTo: routes.analytics.overviewCollection()
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.tools'),
          icon: SettingsMinorIcon,
          id: 'menu-filters-tools',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.mergeValues'),
              isNew: false,
              id: 'menu-filters-merge-values',
              routeTo: routes.mergeValues.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.customRangeSliders'),
              isNew: false,
              id: 'menu-filters-custom-range-sliders',
              routeTo: routes.customRangeSlider.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.swatches'),
              isNew: false,
              isHide: !themeSetupV2,
              id: 'menu-filters-swatch-settings',
              routeTo: routes.swatchSettings.view()
            }
          ]
        }
      ]
    },
    {
      title: i18n.translate('AdminHTML.MainMenuV2.search'),
      id: 'menu-search',
      icon: SearchIcon,
      isNew: false,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.setup'),
          icon: SetupIcon,
          id: 'menu-search-setup',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.searchControl'),
              isNew: false,
              id: 'menu-search-search-control',
              routeTo: routes.searchControl.view(),
              isHide: hideAISearch
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.standardSearch'),
              isNew: false,
              id: 'menu-search-standard-search',
              routeTo: routes.relevanceSettings.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.AIPowerSearch'),
              badge: 'ai',
              isNew: false,
              id: 'menu-search-ai-power-search',
              routeTo: routes.aiSynonym.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.merchandising'),
              isNew: false,
              id: 'menu-search-merchandising',
              routeTo: `${routes.merchandising.view()}?panelID=search`
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.insights'),
          icon: InsightIcon,
          id: 'menu-search-insights',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.analytics'),
              isNew: false,
              id: 'menu-analytics-search',
              routeTo: routes.analytics.overviewSearch()
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.tools'),
          icon: SettingsMinorIcon,
          id: 'menu-search-tools',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.testYourSearch'),
              isNew: false,
              isHide: hideAISearch,
              id: 'menu-search-test-your-search',
              routeTo: routes.testYourSearch.view()
            }
          ]
        }
      ]
    },
    {
      title: i18n.translate('AdminHTML.MainMenuV2.recommendations'),
      id: 'menu-recommendation',
      isNew: false,
      icon: ThumbsUpIcon,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.setup'),
          icon: SetupIcon,
          id: 'menu-recommendation-setup',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.recommendationManageWidgets'),
              isNew: false,
              id: 'menu-recommendation-widget',
              routeTo: routes.recommendationWidgets.list()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.recommendationGeneralSettings'),
              isNew: false,
              id: 'menu-recommendation-general-settings',
              routeTo: routes.recommendationGeneralSettings.settings()
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.insights'),
          icon: InsightIcon,
          id: 'menu-recommendation-insights',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.analytics'),
              isNew: false,
              id: 'menu-analytics-recommendation',
              routeTo: routes.analytics.recommendationOverview()
            }
          ]
        }
      ]
    },
    {
      title: i18n.translate('AdminHTML.MainMenuV2.storePerformance'),
      id: 'menu-analytics',
      icon: AnalyticsIcon,
      isNew: false,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.insights'),
          icon: InsightIcon,
          id: 'menu-performance-insights',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.revenueImpact'),
              isNew: false,
              id: 'menu-analytics-performance-revenue',
              routeTo: routes.analytics.appImpact()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.search'),
              isNew: false,
              id: 'menu-analytics-search',
              routeTo: routes.analytics.overviewSearch()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.filters'),
              isNew: false,
              id: 'menu-analytics-collection',
              routeTo: routes.analytics.overviewCollection()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.recommendation'),
              isNew: false,
              id: 'menu-analytics-recommendation',
              routeTo: routes.analytics.recommendationOverview()
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.tools'),
          icon: SettingsMinorIcon,
          id: 'menu-performance-tools',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.analyticSettings'),
              isNew: false,
              id: 'menu-analytics-settings',
              routeTo: routes.analytics.settings()
            }
          ]
        }
      ]
    }
  ];
};

export const getMenuRight = (i18n, conditions = {}) => {
  const { themeSetupV2 = false, hasShopifyIntegration } = conditions;
  const result = [
    {
      title: '',
      id: 'menu-ai',
      icon: AIIcon,
      routeTo: routes.aiPowered.view()
    },
    {
      id: 'menu-integration',
      icon: PrimaryIcon,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.integration'),
          icon: IntegrationIcon,
          id: 'menu-integration-common',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.shopifyIntegration'),
              isNew: false,
              id: 'menu-integration-theme-setup',
              routeTo: hasShopifyIntegration
                ? routes.shopifyIntegration.view()
                : routes.themeSetup.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.thirdPartyIntegration'),
              isNew: false,
              id: 'menu-integration-third-party',
              routeTo: routes.integration.view()
            }
          ]
        }
      ]
    },
    {
      id: 'menu-settings',
      icon: SettingsIcon,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.sync'),
          icon: SyncIcon,
          id: 'menu-settings-sync',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.syncOverView'),
              isNew: false,
              id: 'menu-settings-sync-overview',
              routeTo: routes.sync.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.syncSettings'),
              isNew: false,
              id: 'menu-settings-sync-settings',
              routeTo: routes.sync.settings()
            }
          ]
        },
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.generalSettings'),
          icon: GlobeIcon,
          id: 'menu-general-settings',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.translation'),
              isNew: false,
              id: 'menu-settings-translation',
              routeTo: routes.translation.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.metafields'),
              isNew: false,
              id: 'menu-settings-metafields',
              routeTo: routes.metafield.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.customSorting'),
              isNew: false,
              isHide: !themeSetupV2,
              id: 'menu-settings-sorting',
              routeTo: routes.customSorting.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.variantsDisplay'),
              isNew: false,
              id: 'menu-settings-variants-display',
              routeTo: routes.variantsDisplay.view()
            }
          ]
        }
      ]
    },
    {
      id: 'menu-account',
      icon: ProfileIcon,
      children: [
        {
          title: i18n.translate('AdminHTML.MainMenuV2.Common.accountSettings'),
          icon: ProfileMinorIcon,
          id: 'menu-account-settings',
          subMenu: [
            {
              title: i18n.translate('AdminHTML.MainMenuV2.accountDetails'),
              isNew: false,
              id: 'menu-account-detail',
              routeTo: routes.account.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.appLanguage'),
              isNew: false,
              id: 'menu-account-app-language',
              routeTo: routes.languageSetting.view()
            },
            {
              title: i18n.translate('AdminHTML.MainMenuV2.subscription'),
              isNew: false,
              id: 'menu-account-subscription',
              routeTo: routes.subscription.plan()
            }
          ]
        }
      ]
    }
  ];

  if (hasShopifyIntegration) {
    result[1].children[0].subMenu.splice(1, 1); // Remove 3rd party integration
  }
  return result;
};
